import { Head, Link, useForm } from '@inertiajs/react'
import { ArrowLeft } from 'lucide-react'
import type { ReactNode } from 'react'

import { PageHeader } from '@/components/PageHeader'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardFooter } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { DashboardLayout } from '@/layouts/DashboardLayout'
import { usersPath } from '@/lib/routes'
import type { UserRole } from '@/types/inertia'

type NewUserForm = {
  first_name: string
  last_name: string
  email: string
  role: UserRole
  send_invitation: boolean
  password: string
}

function UsersNew() {
  const form = useForm<NewUserForm>({
    first_name: '',
    last_name: '',
    email: '',
    role: 'partner',
    send_invitation: true,
    password: '',
  })

  const fieldError = (key: keyof NewUserForm) =>
    (form.errors as Record<string, string>)[key]

  return (
    <>
      <Head title="New user" />

      <div className="mx-auto max-w-2xl px-8 py-10">
        <Link
          href={usersPath()}
          className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to users
        </Link>

        <PageHeader
          title="New user"
          description="Invite someone to the workspace or set their initial password directly."
        />

        <form
          onSubmit={(event) => {
            event.preventDefault()
            form.transform((data) => ({
              user: {
                ...data,
                password: data.send_invitation ? undefined : data.password,
              },
            }))
            form.post(usersPath())
          }}
        >
          <Card>
            <CardContent className="grid gap-5 p-6 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="first_name">First name</Label>
                <Input
                  id="first_name"
                  value={form.data.first_name}
                  onChange={(e) => form.setData('first_name', e.target.value)}
                  autoFocus
                />
                {fieldError('first_name') && (
                  <p className="text-xs text-destructive">{fieldError('first_name')}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="last_name">Last name</Label>
                <Input
                  id="last_name"
                  value={form.data.last_name}
                  onChange={(e) => form.setData('last_name', e.target.value)}
                />
                {fieldError('last_name') && (
                  <p className="text-xs text-destructive">{fieldError('last_name')}</p>
                )}
              </div>

              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  inputMode="email"
                  value={form.data.email}
                  onChange={(e) => form.setData('email', e.target.value)}
                />
                {fieldError('email') && (
                  <p className="text-xs text-destructive">{fieldError('email')}</p>
                )}
              </div>

              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="role">Role</Label>
                <Select
                  id="role"
                  value={form.data.role}
                  onChange={(e) => form.setData('role', e.target.value as UserRole)}
                >
                  <option value="partner">Partner</option>
                  <option value="lp">Limited partner</option>
                  <option value="founder">Founder</option>
                </Select>
                {fieldError('role') && (
                  <p className="text-xs text-destructive">{fieldError('role')}</p>
                )}
              </div>

              <div className="sm:col-span-2 rounded-md border border-border p-4">
                <label className="flex items-start gap-3">
                  <Checkbox
                    checked={form.data.send_invitation}
                    onChange={(e) => form.setData('send_invitation', e.target.checked)}
                    className="mt-0.5"
                  />
                  <span className="flex-1">
                    <span className="block text-sm font-medium">Send invitation email</span>
                    <span className="block text-xs text-muted-foreground">
                      The user receives a link to set their own password. Uncheck to set a
                      password manually.
                    </span>
                  </span>
                </label>

                {!form.data.send_invitation && (
                  <div className="mt-4 space-y-1.5">
                    <Label htmlFor="password">Initial password</Label>
                    <Input
                      id="password"
                      type="password"
                      autoComplete="new-password"
                      value={form.data.password}
                      onChange={(e) => form.setData('password', e.target.value)}
                    />
                    {fieldError('password') && (
                      <p className="text-xs text-destructive">{fieldError('password')}</p>
                    )}
                  </div>
                )}
              </div>
            </CardContent>

            <CardFooter className="justify-end gap-2 border-t border-border pt-4">
              <Link href={usersPath()}>
                <Button type="button" variant="ghost">
                  Cancel
                </Button>
              </Link>
              <Button type="submit" disabled={form.processing}>
                {form.processing
                  ? 'Saving…'
                  : form.data.send_invitation
                    ? 'Send invitation'
                    : 'Create user'}
              </Button>
            </CardFooter>
          </Card>
        </form>
      </div>
    </>
  )
}

export default Object.assign(UsersNew, {
  layout: (page: ReactNode) => <DashboardLayout>{page}</DashboardLayout>,
})
